/**
 * WorkforceSetup — Aperture Bank Chris Bauer wave. Port of setup-screen.jsx:
 * the new-tenant onboarding hub (six steps + progress ribbon). Ops:
 * getSetupProgress / completeSetupStep (REQ-039, kit-served — 4/6 done is
 * Chris's "fresh tenant" landing story).
 */
import { Btn, Icon, T, alpha } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { toast } from "../../shell/toast";
import { can } from "../../shell/access";
import { useWfResource, useBusy } from "./useWorkforce";
import { getSetupProgress, completeSetupStep, type SetupProgressResponse, type SetupStep } from "./wf-api";
import { useT } from "../../lib/i18n";

const STEP_META: Record<SetupStep["status"], { color: string; label: string; labelKey: string }> = {
  done: { color: T.green1, label: "Done", labelKey: "common.actions.done" },
  "in-progress": { color: T.orange1, label: "In progress", labelKey: "workforce.setup.inProgress" },
  todo: { color: T.soft, label: "To do", labelKey: "workforce.setup.toDo" },
};

export default function WorkforceSetup() {
  const t = useT();
  const { data, loading, error, reload } = useWfResource<SetupProgressResponse>("wfsetup", getSetupProgress);
  const { busy, run } = useBusy();
  const canWrite = can("setup.write");

  const complete = (step: SetupStep) => {
    void run(step.id, async () => {
      try {
        await completeSetupStep(step.id);
        toast({ tone: "success", title: t("workforce.setup.stepCompleted", "Step completed"), subtitle: step.label });
        reload();
      } catch (e) { toast({ tone: "danger", title: t("workforce.setup.couldnTCompleteTheStep", "Couldn't complete the step"), subtitle: e instanceof Error ? e.message : String(e) }); }
    });
  };

  return (
    <Screen rail="ab-setup">
      <PageHeader title={t("workforce.setup.setup", "Setup")} subtitle={t("workforce.setup.getTheWorkforcePlatformLiveSixStepsInOrderWithWhatS", "Get the workforce platform live — six steps, in order, with what's blocking called out.")} breadcrumb={null} />
      <DataSourceStrip screen="wfsetup" ops={["getSetupProgress", "completeSetupStep"]} reqId="REQ-039" />
      <div style={{ flex: 1, overflow: "auto", padding: "14px 24px 32px", display: "flex", flexDirection: "column", gap: 16, maxWidth: 860 }}>
        {error ? <div style={{ padding: 40, textAlign: "center", color: T.soft }}>{t("workforce.setup.couldnTLoadSetupProgress", "Couldn't load setup progress:")}{" "}{error}</div>
          : loading || !data ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("common.status.loading", "Loading…")}</div>
          : <>
            <div style={{ border: `1px solid ${T.border}`, borderRadius: 12, background: T.white, padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
                <b style={{ fontSize: 14, color: T.n1 }}>{t("workforce.setup.workspaceSetup", "Workspace setup")}</b>
                <span style={{ fontSize: 20, fontWeight: 700, color: data.pctComplete >= 100 ? T.green1 : T.p1, fontFamily: T.fontMono }}>{data.pctComplete}%</span>
              </div>
              <div style={{ height: 8, borderRadius: 999, background: T.n6, overflow: "hidden" }}>
                <span style={{ display: "block", height: "100%", width: `${Math.min(100, data.pctComplete)}%`, background: data.pctComplete >= 100 ? T.green1 : T.p1, transition: "width 300ms" }} />
              </div>
              {data.blocking.length > 0 ? (
                <div style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 12, padding: "10px 12px", borderRadius: 8, background: alpha(T.orange1, 10) }}>
                  <Icon.warn size={13} style={{ color: T.orange1, flexShrink: 0, marginTop: 1 }} />
                  <span style={{ fontSize: 12.5, color: T.n1 }}><b>{t("workforce.setup.blockingGoLive", "Blocking go-live:")}</b> {data.blocking.join(" · ")}</span>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, padding: "10px 12px", borderRadius: 8, background: alpha(T.green1, 10) }}>
                  <Icon.check size={13} style={{ color: T.green1 }} />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: T.n1 }}>{t("workforce.setup.nothingBlockingThePlatformIsLive", "Nothing blocking — the platform is live.")}</span>
                </div>
              )}
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {data.steps.map((s, i) => {
                const m = STEP_META[s.status];
                return (
                  <div key={s.id} data-testid="wfsetup-step" data-id={s.id} style={{ display: "flex", gap: 14, alignItems: "flex-start", padding: "14px 18px", background: T.white, border: `1px solid ${T.border}`, borderRadius: 12, opacity: s.status === "done" ? 0.75 : 1 }}>
                    <span style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0, display: "grid", placeItems: "center", background: s.status === "done" ? alpha(T.green1, 14) : T.n6, color: m.color, fontSize: 13, fontWeight: 700, fontFamily: T.fontMono }}>
                      {s.status === "done" ? <Icon.check size={14} /> : i + 1}
                    </span>
                    <span style={{ display: "flex", flexDirection: "column", gap: 3, flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
                        <b style={{ fontSize: 13.5, color: T.n1 }}>{s.label}</b>
                        <span style={{ fontSize: 10.5, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.05, color: m.color }}>{t(m.labelKey, m.label)}</span>
                      </span>
                      <span style={{ fontSize: 12.5, color: T.n2, lineHeight: 1.5 }}>{s.detail}</span>
                      <span style={{ fontSize: 11, color: T.soft, fontFamily: T.fontMono }}>{s.href}</span>
                    </span>
                    {s.status !== "done" ? (
                      <Btn size="sm" variant={s.status === "in-progress" ? "primary" : "secondary"} testid="wfsetup-complete" disabled={!canWrite || busy !== null} title={canWrite ? undefined : t("workforce.workforceSetup.requiresTheSetupWriteCapability", "Requires the setup.write capability")} onClick={() => complete(s)}>
                        {busy === s.id ? t("workforce.workforceSetup.saving", "Saving…") : t("workforce.workforceSetup.markComplete", "Mark complete")}
                      </Btn>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </>}
      </div>
    </Screen>
  );
}
